export const GPX_MAX_BYTES = 10 * 1024 * 1024;
export const GPX_MAX_MAP_POINTS = 2_000;
export const GPX_MAX_SAMPLE_POINTS = 100;

export type GpxPoint = {
  lat: number;
  lon: number;
  ele?: number;
  time?: string;
  name?: string;
};

export type GpxTrack = {
  name?: string;
  points: GpxPoint[];
  samplePoints: GpxPoint[];
};

export type ParseGpxResult = {
  tracks: GpxTrack[];
  waypoints: GpxPoint[];
};

type ParserOptions = { maxMapPoints?: number; maxSamplePoints?: number };
type TextField = { tag: 'name' | 'ele' | 'time'; depth: number; buffer: string; point?: GpxPoint; track?: GpxTrack };

function localName(raw: string): string {
  const withoutPrefix = raw.trim().split(':').pop() ?? raw;
  return withoutPrefix.toLowerCase();
}

function decodeEntities(value: string): string {
  return value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}

function parseAttributes(raw: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const attrPattern = /([\w:.-]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
  let match: RegExpExecArray | null;
  while ((match = attrPattern.exec(raw))) {
    attrs[localName(match[1]!)] = decodeEntities(match[2] ?? match[3] ?? '');
  }
  return attrs;
}

function thinPoints(points: GpxPoint[], maxPoints: number): GpxPoint[] {
  if (points.length <= maxPoints) return points;
  if (maxPoints < 2) return points.slice(0, maxPoints);
  const output: GpxPoint[] = [];
  const step = (points.length - 1) / (maxPoints - 1);
  for (let index = 0; index < maxPoints; index += 1) {
    output.push(points[Math.round(index * step)]!);
  }
  return output;
}

function parsePoint(attrs: Record<string, string>): GpxPoint {
  const lat = Number(attrs.lat);
  const lon = Number(attrs.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) {
    throw new Error('GPX contains a point with invalid latitude or longitude.');
  }
  return { lat, lon };
}

function finishTextField(field: TextField): void {
  const text = decodeEntities(field.buffer.replace(/\s+/g, ' ').trim());
  if (!text) return;
  if (field.tag === 'name') {
    if (field.point) field.point.name = text;
    else if (field.track) field.track.name = text;
  } else if (field.tag === 'ele' && field.point) {
    const elevation = Number(text);
    if (Number.isFinite(elevation)) field.point.ele = elevation;
  } else if (field.tag === 'time' && field.point) {
    field.point.time = text;
  }
}

function appendText(fields: TextField[], text: string): void {
  if (!text) return;
  const active = fields[fields.length - 1];
  if (active) active.buffer += text;
}

function addPointToTrack(track: GpxTrack, point: GpxPoint): void {
  track.points.push(point);
}

/**
 * Small streaming-style GPX scanner. It intentionally avoids DOMParser so the same
 * implementation works in Expo native, web, and Vitest without a DOM polyfill.
 */
export function parseGpx(xml: string, optionsOrMaxPoints: ParserOptions | number = {}): ParseGpxResult {
  if (typeof xml !== 'string' || !xml.trim()) throw new Error('GPX file is empty.');
  if (xml.length > GPX_MAX_BYTES) throw new Error('GPX file is larger than 10 MB.');
  const options: ParserOptions = typeof optionsOrMaxPoints === 'number'
    ? { maxMapPoints: optionsOrMaxPoints }
    : optionsOrMaxPoints;
  const maxMapPoints = Math.max(2, Math.floor(options.maxMapPoints ?? GPX_MAX_MAP_POINTS));
  const maxSamplePoints = Math.max(2, Math.floor(options.maxSamplePoints ?? GPX_MAX_SAMPLE_POINTS));

  const rootMatch = xml.match(/<([\w:.-]+)\b[^>]*>/);
  if (!rootMatch || localName(rootMatch[1]!) !== 'gpx') throw new Error('Not a GPX file.');

  const stack: string[] = [];
  const fields: TextField[] = [];
  const tracks: GpxTrack[] = [];
  const waypoints: GpxPoint[] = [];
  let currentTrack: GpxTrack | undefined;
  let currentPoint: GpxPoint | undefined;
  let sawRoot = false;
  let cursor = 0;

  const startTag = (rawTag: string, selfClosing: boolean) => {
    const match = rawTag.match(/^<\s*([\w:.-]+)([\s\S]*?)\/?\s*>$/);
    if (!match) throw new Error('Malformed GPX tag.');
    const tag = localName(match[1]!);
    const attrs = parseAttributes(match[2] ?? '');
    const depth = stack.length;
    if (tag === 'gpx') sawRoot = true;
    if (tag === 'trk' || tag === 'rte') {
      currentTrack = { points: [], samplePoints: [] };
      tracks.push(currentTrack);
    } else if (tag === 'trkpt' || tag === 'rtept' || tag === 'wpt') {
      const point = parsePoint(attrs);
      currentPoint = point;
      if (tag === 'wpt') waypoints.push(point);
      else if (!currentTrack) throw new Error('GPX point is outside a track or route.');
      else addPointToTrack(currentTrack, point);
    } else if (tag === 'name' || tag === 'ele' || tag === 'time') {
      fields.push({ tag, depth, buffer: '', point: currentPoint, track: currentTrack });
    }
    if (!selfClosing) stack.push(tag);
    else if (tag === 'name' || tag === 'ele' || tag === 'time') finishTextField(fields.pop()!);
    else if (tag === 'trkpt' || tag === 'rtept' || tag === 'wpt') {
      currentPoint = undefined;
    }
  };

  const endTag = (rawTag: string) => {
    const match = rawTag.match(/^<\/\s*([\w:.-]+)\s*>$/);
    if (!match) throw new Error('Malformed GPX closing tag.');
    const tag = localName(match[1]!);
    const expected = stack.pop();
    if (!expected || expected !== tag) throw new Error(`Malformed GPX: unexpected closing tag </${tag}>.`);
    if (fields.length && fields[fields.length - 1]!.depth === stack.length && fields[fields.length - 1]!.tag === tag) {
      finishTextField(fields.pop()!);
    }
    if (tag === 'trkpt' || tag === 'rtept' || tag === 'wpt') {
      currentPoint = undefined;
    } else if (tag === 'trk' || tag === 'rte') {
      currentTrack = undefined;
    }
  };

  while (cursor < xml.length) {
    const open = xml.indexOf('<', cursor);
    if (open < 0) {
      appendText(fields, xml.slice(cursor));
      cursor = xml.length;
      break;
    }
    appendText(fields, xml.slice(cursor, open));
    if (xml.startsWith('<![CDATA[', open)) {
      const close = xml.indexOf(']]>', open + 9);
      if (close < 0) throw new Error('Malformed GPX CDATA section.');
      appendText(fields, xml.slice(open + 9, close));
      cursor = close + 3;
      continue;
    }
    const close = xml.indexOf('>', open + 1);
    if (close < 0) throw new Error('Malformed GPX tag.');
    const raw = xml.slice(open, close + 1);
    if (raw.startsWith('<!--')) {
      const commentEnd = xml.indexOf('-->', open + 4);
      if (commentEnd < 0) throw new Error('Malformed GPX comment.');
      cursor = commentEnd + 3;
      continue;
    }
    if (raw.startsWith('<?') || raw.startsWith('<!DOCTYPE') || raw.startsWith('<![')) {
      cursor = close + 1;
      continue;
    }
    if (raw.startsWith('</')) endTag(raw);
    else startTag(raw, /\/\s*>$/.test(raw));
    cursor = close + 1;
  }

  if (!sawRoot || stack.length > 0 || fields.length > 0) throw new Error('Malformed or incomplete GPX XML.');
  const nonEmptyTracks = tracks
    .filter((track) => track.points.length > 0)
    .map((track) => ({
      ...track,
      points: thinPoints(track.points, maxMapPoints),
      samplePoints: thinPoints(track.points, maxSamplePoints),
    }));
  if (!nonEmptyTracks.length && !waypoints.length) throw new Error('GPX file contains no route points.');
  return { tracks: nonEmptyTracks, waypoints: thinPoints(waypoints, maxMapPoints) };
}

